import React, { useReducer } from 'react';
import gql from 'graphql-tag';
import cx from 'classnames';
import {
  ButtonGroup,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import { Alert, Button, Toggle } from '@storable/canaveral-components';
import FormFieldLabelTooltip from '../FormFieldLabelTooltip';
import Icon from '../../../modules/react/components/stateless/Icon';
import reducer, { getInitialState, SELECTORS, TYPES } from './reducer';
import styles from './styles.scss';

const ALERT_CONTEXTS = ['danger', 'warning', 'info', 'secondary', 'primary'];

const IntegrationTable = ({
  integration,
  onChangeAlertContext,
  onChangeAlertStatus,
  onChangeCurrentAlert,
  onChangeDefaultAlert,
}) => {
  const {
    alert: {
      defaultMessage: initialDefaultAlert,
      message: initialCurrentAlert,
    },
    displayName,
    name,
  } = integration;

  const [state, dispatch] = useReducer(reducer, getInitialState(integration));

  const onActiveChange = (e) => {
    dispatch({ type: TYPES.activeToggle });
    onChangeAlertStatus(e.target.checked);
  };

  const onContextChange = context => () => {
    dispatch({ type: TYPES.contextUpdate, value: context });
    onChangeAlertContext(context);
  };

  const onCurrentAlertApplyDefault = () => dispatch({
    type: TYPES.currentAlertApplyDefault,
    value: SELECTORS.getDefaultAlert(state),
  });

  const onCurrentAlertCancelEdit = () => dispatch({
    type: TYPES.currentAlertCancelEdit,
    value: initialCurrentAlert,
  });

  const onCurrentAlertEdit = () => dispatch({ type: TYPES.currentAlertEdit });

  const onCurrentAlertSave = () => {
    dispatch({ type: TYPES.currentAlertSave });
    onChangeCurrentAlert(SELECTORS.getCurrentAlert(state));
  };

  const onCurrentAlertUpdate = e => dispatch({
    type: TYPES.currentAlertUpdate,
    value: e.target.value,
  });

  const onDefaultAlertCancelEdit = () => dispatch({
    type: TYPES.defaultAlertCancelEdit,
    value: initialDefaultAlert,
  });

  const onDefaultAlertEdit = () => dispatch({ type: TYPES.defaultAlertEdit });

  const onDefaultAlertSave = () => {
    dispatch({ type: TYPES.defaultAlertSave });
    onChangeDefaultAlert(SELECTORS.getDefaultAlert(state));
  };

  const onDefaultAlertUpdate = e => dispatch({
    type: TYPES.defaultAlertUpdate,
    value: e.target.value,
  });

  return (
    <Col className={styles.container} md={6} sm={12}>
      <Row className={styles.title}>
        <Col xs={6}>
          <h3>{displayName}</h3>
        </Col>
        <Col xs={6}>
          <div className={styles.alertToggle}>
            <span className={styles.alertStatus}>
              {SELECTORS.getActive(state) ? 'ALERT ACTIVE!' : 'Activate Alert'}
            </span>
            <Toggle
              checked={SELECTORS.getActive(state)}
              onClick={onActiveChange}
            />
          </div>
        </Col>
      </Row>
      <div className={styles.alertGroup}>
        <Row>
          <Col xs={12}>
            <Form.Group
              controlId={`${name}-default-alert`}
              className={styles.formGroup}
            >
              <Form.Label>Default Alert</Form.Label>
              <FormFieldLabelTooltip
                className={styles.tooltip}
                text="The Default Alert is meant to hold a generic alert for the integration that can be used to create messaging on the website quickly. It is never displayed. When editing the Current Alert, click the Apply Default button to swap out the Current Alert text with the Default Alert text."
              />
              <Form.Control
                className={styles.alertInput}
                as="textarea"
                disabled={!SELECTORS.getDefaultAlertEditing(state)}
                onChange={onDefaultAlertUpdate}
                value={SELECTORS.getDefaultAlert(state)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <ButtonGroup className={cx(styles.buttonGroup, styles.pullRight)}>
              {SELECTORS.getDefaultAlertEditing(state) ? (
                <>
                  <Button
                    context="neutral"
                    data-testid="default-alert-edit-cancel"
                    onClick={onDefaultAlertCancelEdit}
                  >
                    <Icon id="times" label="Cancel" />
                  </Button>
                  <Button
                    className={styles.btnPositive}
                    data-testid="default-alert-edit-save"
                    variant="success"
                    onClick={onDefaultAlertSave}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  context="neutral"
                  data-testid="default-alert-edit"
                  onClick={onDefaultAlertEdit}
                >
                  <Icon id="pencil" label="Edit" />
                </Button>
              )}
            </ButtonGroup>
          </Col>
        </Row>
      </div>
      <div className={styles.alertGroup}>
        <Row>
          <Col xs={12}>
            <Form.Group
              controlId={`${name}-current-alert`}
              className={styles.formGroup}
              validationState
            >
              <Form.Label>Current Alert</Form.Label>
              <FormFieldLabelTooltip
                className={styles.tooltip}
                text="The Current Alert will display on affected widgets when the integration alert is activated above."
              />
              <Form.Control
                className={styles.alertInput}
                as="textarea"
                disabled={!SELECTORS.getCurrentAlertEditing(state)}
                onChange={onCurrentAlertUpdate}
                value={SELECTORS.getCurrentAlert(state)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <ButtonGroup className={cx(styles.buttonGroup, styles.pullRight)}>
              {SELECTORS.getCurrentAlertEditing(state) ? (
                <>
                  <Button
                    variant="neutral"
                    data-testid="current-alert-edit-cancel"
                    onClick={onCurrentAlertCancelEdit}
                  >
                    <Icon id="times" label="Cancel" />
                  </Button>
                  <Button
                    className={styles.btnNeutral}
                    variant="neutral"
                    data-testid="current-alert-edit-revert"
                    onClick={onCurrentAlertApplyDefault}
                  >
                    Apply Default
                  </Button>
                  <Button
                    className={styles.btnPositive}
                    variant="positive"
                    data-testid="current-alert-edit-save"
                    onClick={onCurrentAlertSave}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  variant="neutral"
                  data-testid="current-alert-edit"
                  onClick={onCurrentAlertEdit}
                >
                  <Icon id="pencil" label="Edit" />
                </Button>
              )}
            </ButtonGroup>
          </Col>
        </Row>
      </div>
        <Row>
          <Col xs={12}>
            <span className={styles.label}>Alert Context</span>
              <FormFieldLabelTooltip
                className={styles.tooltip}
                text="The Alert Context sets the color scheme for the alert. Click any of the buttons below to change the alert context."
              />
            <ButtonGroup className={cx(styles.buttonGroup, styles.contextGroup)}>
              {ALERT_CONTEXTS.map(contextName => (
                <Button
                  data-testid={`alert-context-${contextName}`}
                  onClick={onContextChange(contextName)}
                  className={styles[`${contextName}Alert`]}
                >
                  <Alert
                    className={styles.alertContext}
                    variant={contextName}
                  >
                    {contextName}
                  </Alert>
                </Button>
              ))}
            </ButtonGroup>
          </Col>
        </Row>
      <div className={styles.alertGroup}>
        <Row>
          <Col xs={12}>
            <span className={styles.label}>Preview</span>
            <Alert variant={SELECTORS.getContext(state)} className={styles[`${SELECTORS.getContext(state)}Alert`]}>
              <span>{SELECTORS.getCurrentAlert(state)}</span>
              <span>
                <i> Any component-specific messaging will trail the integration alert like so.</i>
              </span>
            </Alert>
          </Col>
        </Row>
      </div>
    </Col>
  );
};

IntegrationTable.fragments = {
  integration: gql`
    fragment IntegrationTableIntegrationFields on Integration {
      alert {
        active
        context
        defaultMessage
        message
      }
      displayName
      name
    }
  `,
};

export default IntegrationTable;
